/**
 * ============================================================================
 * GUIDED TOUR — STEP DATA MODEL (section-spotlight redesign)
 * ============================================================================
 * Replaces driver.js's `DriveStep`. A `TourStep` is a structural superset of
 * the old shape, so existing single-element steps (`{ element, popover }`)
 * remain valid literals — they render as a single-target spotlight in the new
 * chrome. Form-heavy phases add `callouts[]` to annotate several fields inside
 * ONE highlighted section, all shown at once.
 *
 * Three step shapes:
 *   - Section step:  `target` (a container) + `callouts` → whole card spotlit,
 *                    a numbered box per field.
 *   - Single step:   `target` / `element` only → one control spotlit.
 *   - Welcome step:  neither → a centered, modal-style card (no cutout).
 *
 * Targets are `data-tour` CSS selectors (see tourStore.ts for the convention).
 * ============================================================================
 */

export type TourSide = 'top' | 'bottom' | 'left' | 'right';
export type TourAlign = 'start' | 'center' | 'end';

/** One annotation box pinned to a sub-element inside a highlighted section. */
export interface TourCallout {
  /** `data-tour` selector of the field this callout points at. */
  target: string;
  title: string;
  description?: string;
  /** Renders an "optional" badge — explains the field without implying it's required. */
  optional?: boolean;
  /** Override the auto number (defaults to the callout's index + 1). */
  number?: number;
  /** Preferred side to place the box relative to its field. Default: auto. */
  side?: TourSide;
}

/**
 * One branch of a fork in the tour.
 *
 * The tour is otherwise a straight line, which is fine while every user wants
 * the same thing — but it is wrong the moment a chapter only applies to SOME
 * people. The workspaces chapter is the case in point: most users run one
 * company, and walking them through "add a second brand" as a mandatory,
 * hands-on step made them mime creating a company they do not want.
 *
 * A step with `choices` asks instead of assuming. The overlay renders one
 * button per choice INSTEAD of Next; each choice decides where the tour goes
 * (usually `nextStep()` to continue into the chapter, or `jumpToStep()` to skip
 * past it). Back and Skip are untouched — a fork is still never a trap.
 */
export interface TourChoice {
  label: string;
  onSelect: () => void;
  /** The quieter, secondary-styled option. Use for the "no thanks" branch. */
  subtle?: boolean;
}

export interface TourPopover {
  title?: string;
  description?: string;
  /** Placement hint for the caption/popover relative to the target. */
  side?: TourSide;
  align?: TourAlign;
  nextBtnText?: string;
  prevBtnText?: string;
  /**
   * Fork the tour. When present the overlay renders these buttons in place of
   * Next, and the step does not auto-advance — the chosen branch decides.
   */
  choices?: readonly TourChoice[];
  /**
   * Terminal action for the Next button. When present the overlay calls this
   * INSTEAD of auto-advancing — every current use ends or hands off the tour
   * (endTour / chainTo / triggerCheckpoint), matching driver.js semantics.
   */
  onNextClick?: () => void;
  onPrevClick?: () => void;
  /**
   * Legacy driver.js field — which chrome buttons to show. The overlay always
   * renders Back / Next / Skip, so this is accepted for source compatibility
   * with existing step literals but otherwise ignored.
   */
  showButtons?: readonly string[];
}

/**
 * A hands-on gate. When a step has one, the tour will NOT let the user click
 * past it — the Next button is replaced by `hint` until `done()` returns true,
 * at which point the tour advances itself. This turns the tour from "read this"
 * into a game-style tutorial where the user actually performs a full run.
 *
 * `done()` is polled, so it can read stores or the DOM. Keep it cheap.
 * "Skip tour" always remains available — a gate never traps the user.
 */
export interface TourAction {
  /** True once the user has actually performed this step's action. */
  done: () => boolean;
  /** Shown in place of the Next button until the action is done. */
  hint: string;
}

export interface TourStep {
  /**
   * The spotlight target — a container for a section step, a single control
   * otherwise. `element` is the legacy alias (kept so old literals compile);
   * the overlay reads `target ?? element`.
   */
  target?: string;
  element?: string;
  /** Field annotations shown all at once inside a section target. */
  callouts?: TourCallout[];
  popover?: TourPopover;
  /**
   * Require the user to actually DO this step before moving on. Without it the
   * step is informational and Next works normally.
   */
  action?: TourAction;
  /**
   * Seal the cutout: show the control lit up but make it NOT operable. Use this
   * ONLY where the spotlit control exposes a way off the journey — e.g. the
   * workspace switcher, whose menu can navigate to settings before it's that
   * chapter's turn. Everything else stays interactive: a hands-on tutorial that
   * blocks the very control it's describing ("pick a persona" — and you can't)
   * is worse than no rails at all.
   */
  sealed?: boolean;
  /**
   * Fired when this step becomes active — used on mobile to open the sidebar
   * menu that hosts a sidebar-mounted target, then reflow. Unlike driver.js
   * this receives no args; call the overlay's reflow via `requestTourReflow()`.
   */
  onHighlightStarted?: () => void;
  /**
   * Survive the DOM guard even though the target isn't on the page YET.
   *
   * `resolveSteps` runs ONCE when a phase starts and drops any step whose target
   * is missing — which is right for optional content (no saved posts, no
   * personas) but wrong for a target the tour is about to CREATE. The create-
   * workspace sub-flow gates on the user opening a modal, so the modal's fields
   * do not exist at phase start; without this they'd be filtered out and the
   * walkthrough would collapse to nothing. The overlay's pause/resume already
   * covers the gap if such a target never shows up.
   */
  lateMount?: boolean;
}

/** Resolve a step's spotlight selector regardless of which alias it used. */
export function stepTarget(step: TourStep): string | undefined {
  return step.target ?? step.element;
}

/**
 * The first VISIBLE element matching a selector — not merely the first one in the
 * document.
 *
 * Responsive UIs render the same control twice and hide one: the ZupaPost/Case
 * Study toggle is `hidden lg:flex` in the header and lives in the side pane on
 * mobile; the settings nav is `hidden lg:block`. A plain `querySelector` happily
 * returns the hidden copy, which is IN the DOM but measures 0×0 — so the tour
 * would pass its DOM guard, then draw a cutout at zero size in the top-left
 * corner. Every step that spotlights a breakpoint-swapped control is exposed to
 * this, so it's fixed once, here, rather than per step.
 *
 * `offsetParent === null` catches `display: none` (and any hidden ancestor); the
 * size check catches elements that are technically laid out but have collapsed.
 * Falls back to the first match so a step targeting something legitimately
 * zero-size still behaves as it did.
 */
export function queryVisible(selector: string): Element | null {
  const all = document.querySelectorAll(selector);
  for (const el of all) {
    const he = el as HTMLElement;
    if (he.offsetParent === null && getComputedStyle(he).position !== 'fixed') continue;
    const r = he.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return all[0] ?? null;
}
